
import React, { useRef, useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Camera, Loader2 } from 'lucide-react';
import { useUpdateProfile } from '@/hooks/useUpdateProfile';
import { toast } from '@/components/ui/sonner';
import { getStorageUrlWithCacheBuster } from '@/utils/storageUtils';

interface AvatarUploadProps {
  avatarUrl: string | null;
  name: string;
  onAvatarChange?: (url: string) => void;
  size?: 'sm' | 'md' | 'lg';
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({ 
  avatarUrl, 
  name, 
  onAvatarChange, 
  size = 'lg' 
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadAvatar, uploading } = useUpdateProfile();
  // Используем проксированный URL с обходом кэша
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    avatarUrl ? getStorageUrlWithCacheBuster(avatarUrl) : null
  );
  
  // Обновляем локальное состояние при изменении props, используя проксированный URL
  useEffect(() => {
    if (avatarUrl) {
      const newUrl = getStorageUrlWithCacheBuster(avatarUrl);
      setPreviewUrl(newUrl);
    } else {
      setPreviewUrl(null);
    }
  }, [avatarUrl]);
  
  const sizes = {
    sm: 'h-16 w-16',
    md: 'h-20 w-20',
    lg: 'h-24 w-24'
  };
  
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const file = e.target.files[0];
    if (!file) return;
    
    // Проверка типа файла
    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение');
      return;
    }
    
    // Проверка размера файла (максимум 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Размер файла не должен превышать 5MB');
      return;
    }
    
    // Создаем локальный превью URL для немедленного отображения
    const localPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(localPreviewUrl);
    
    // Загружаем файл на сервер
    const newAvatarUrl = await uploadAvatar(file);
    
    // Обновляем URL после успешной загрузки на сервер
    if (newAvatarUrl) {
      setPreviewUrl(newAvatarUrl);
      if (onAvatarChange) {
        onAvatarChange(newAvatarUrl);
      }
    } else {
      // Если загрузка не удалась, возвращаем старое изображение
      if (avatarUrl) {
        const cachedUrl = getStorageUrlWithCacheBuster(avatarUrl);
        setPreviewUrl(cachedUrl);
      } else {
        setPreviewUrl(null);
      }
    }
    
    // Освобождаем ресурс локального превью
    URL.revokeObjectURL(localPreviewUrl);
    
    // Сбрасываем значение input, чтобы можно было выбрать тот же файл снова
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };
  
  return (
    <div className="flex flex-col items-center space-y-2">
      <Avatar className={sizes[size]}>
        <AvatarImage 
          src={previewUrl || undefined} 
          alt={name}
          className="object-cover"
        />
        <AvatarFallback>{getInitials(name)}</AvatarFallback>
      </Avatar>
      
      <div>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading}
        />
        <Button 
          variant="outline" 
          size="sm" 
          className="text-xs"
          onClick={handleButtonClick}
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Загрузка...
            </>
          ) : (
            <>
              <Camera className="h-3 w-3 mr-1" />
              Изменить фото
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
